import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { TabEnumFe, TabType } from "@intelligems/ig-types";
import { DashboardPageName } from "@src/api/types/dashboards";
import { setAnalyticsSubpage } from "@src/store/slices/dashboardFilters";
import {
  setExperienceActionMode,
  setExperienceTab,
  setExperienceViewMode,
} from "@src/store/slices/experiences";
import { IExperienceState } from "@src/store/slices/experiences/types";

export const useSetViewActionAnyExperience = (
  experienceState: IExperienceState
) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const action = searchParams.get("action");
  const tab = searchParams.get("tab");
  const subpage = searchParams.get("subpage");

  useEffect(() => {
    if (experienceState.isSuccess && experienceState.experience) {
      if (action === "edit" || Boolean(experienceState.experience.id)) {
        dispatch(setExperienceActionMode("edit"));
        if (tab) {
          dispatch(setExperienceTab({ tab: tab as TabType, mode: "replace" }));
          if ((tab as TabType) === TabEnumFe.ANALYTICS) {
            if (subpage) {
              dispatch(
                setAnalyticsSubpage({
                  key: "pageName",
                  value: subpage as DashboardPageName,
                })
              );
            }
          }
        } else {
          if (
            experienceState.experience.category === "experiment" &&
            experienceState.experience.status !== "pending"
          ) {
            dispatch(
              setExperienceTab({ tab: TabEnumFe.ANALYTICS, mode: "replace" })
            );
            if (subpage) {
              dispatch(
                setAnalyticsSubpage({
                  key: "pageName",
                  value: subpage as DashboardPageName,
                })
              );
            }
          } else {
            if (experienceState.experience.category === "experiment") {
              dispatch(
                setExperienceTab({
                  tab: TabEnumFe.TEST_GROUPS,
                  mode: "replace",
                })
              );
            } else {
              dispatch(
                setExperienceTab({ tab: TabEnumFe.MODS, mode: "replace" })
              );
            }
          }
        }

        if (
          experienceState.experience.isExternal ||
          experienceState.experience.status === "ended"
        ) {
          dispatch(setExperienceViewMode("view"));
        }

        if ((tab as TabType) === TabEnumFe.ANALYTICS) {
          dispatch(
            setExperienceTab({ tab: TabEnumFe.ANALYTICS, mode: "replace" })
          );
          if (subpage) {
            dispatch(
              setAnalyticsSubpage({
                key: "pageName",
                value: subpage as DashboardPageName,
              })
            );
          }
        }
      } else {
        dispatch(setExperienceActionMode("create"));
        dispatch(setExperienceViewMode("edit"));
        if (tab) {
          dispatch(setExperienceTab({ tab: tab as TabType, mode: "replace" }));
        } else if (experienceState.experience.category === "experiment") {
          dispatch(
            setExperienceTab({ tab: TabEnumFe.TEST_GROUPS, mode: "replace" })
          );
        } else {
          dispatch(setExperienceTab({ tab: TabEnumFe.MODS, mode: "replace" }));
        }
      }
    }
    // `experienceState.experience` is re-derived (new object identity) by
    // `initPersonalizationFromDto`/similar resets whenever the underlying
    // experience changes — e.g. navigating in-app from an active experiment
    // straight into "create personalization" resets category/status/id on
    // the shared `experience` slice a beat after this effect's first run.
    // Depending only on `isSuccess` misses that reset (it stays `true`
    // throughout), so the effect fired once against the stale experiment
    // data and never re-ran to correct the tab. Depending on the actual
    // fields the body branches on makes it re-evaluate once the reset lands.
  }, [
    experienceState.isSuccess,
    experienceState.experience?.id,
    experienceState.experience?.category,
    experienceState.experience?.status,
    experienceState.experience?.isExternal,
    action,
    tab,
    subpage,
  ]);
};
